import {
  AppBar,
  Box,
  Button,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useContext,
  useRef,
} from "react";
import { makeStyles, useTheme } from "@mui/styles";

import SlidingPaneComponent from "../../sharedComponents/SlidingPane/SlidingPane";
import AddEditUser from "./AddEditUser";
import { UserManagementService } from "../../services/userManagement.Services";
import UserCard from "./UserCard";
import Loader from "../../sharedComponents/loader/loader";
import Paging from "../../sharedComponents/Pagination/paging";
import EmptyUI from "../../sharedComponents/emptyStateUIContainers/EmptyUI";

//... Icons imports
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import HomeIcon from "@mui/icons-material/Home";
import ClearIcon from "@mui/icons-material/Clear";
import { AddCircleOutline as AddCircleOutlineIcon } from "@mui/icons-material";

//.. Context provider
import AppDataContext from "../../common/appContext";

//... Shared compoentes
import AlertSnackbar from "../../sharedComponents/Snackbar/AlertSnackbar";
import ConfirmationDialog from "../../sharedComponents/dialog/ConfirmationDialog";

const useStyles = makeStyles((theme) => ({
  appbarBackground: {
    backgroundColor: "#fff",
    color: "#1d428a",
  },
  title: {
    flexGrow: 1,
  },
  pagination: {
    "& .MuiPaginationItem-root": {
      fontSize: theme.spacing(1.5),
      minWidth: theme.spacing(2.5),
      height: theme.spacing(2.5),
    },
    "& .MuiSelect-select": {
      padding: theme.spacing(0.5, 1),
      fontSize: theme.spacing(1.5),
    },
  },
}));

const initalFormData = {
  userId: 0,
  userType: "",
  firstName: "",
  lastName: "",
  loginId: "",
  email: "",
  active: false,
  teamId: 0,
  title: "",
  rankLevel: 0,
  userRole: [
    {
      userRoleId: 0,
      userId: 0,
      roleId: "",
      activeForBallotEmail: false,
      leagueId: "",
    },
  ],
};

const mappedRoleIdsToUserType = {
  Admin: 1,
  Team: 3,
  League: 2,
};

function UserManagementContainer() {
  const classes = useStyles();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const { user } = useContext(AppDataContext);

  const [loading, setLoading] = useState(false);
  const [userDetails, setUserDetails] = useState(initalFormData);
  const [userRoles, setUserRoles] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [usersData, setUsersData] = useState([]);

  //..Filter
  const [searchText, setSearchText] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(null);

  const [openDeleteRoleDialog, setOpenDeleteRoleDialog] = useState(false);
  const [pagingInfo, setPagingInfo] = useState({
    currentPage: 1,
    rowsPerPage: 40,
    noOfPages: 1,
  });

  //... Alert State holders.
  const [alert, setAlert] = useState({
    open: false,
    type: "warning",
    message: "",
  });

  const deleteRoleRef = useRef(null);

  useEffect(() => {
    const fetchDefaultDependencies = async () => {
      try {
        setLoading(true);
        const [rolesRes, teamsRes, usersRes] = await Promise.all([
          UserManagementService.getUserRoles(),
          UserManagementService.getTeams(),
          UserManagementService.getUserWithRoles(),
        ]);

        setUserRoles(rolesRes.data || []);
        setAllTeams(teamsRes.data || []);
        setUsersData(usersRes.data || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDefaultDependencies();
  }, []);

  const paginatedUsers = useMemo(() => {
    const search = searchText.toLowerCase();
    const selectedRoleId = selectedRole;
    const selectedStatusVal = selectedStatus;

    const filtered = usersData.filter((user) => {
      const fullName = `${user.firstName ?? ""} ${
        user.lastName ?? ""
      }`.toLowerCase();
      const email = user.email?.toLowerCase() ?? "";
      const matchesSearchText =
        fullName.includes(search) || email.includes(search);

      //@ Check if the user has the selected roleId in their userRole array
      const matchesRoleId = selectedRoleId
        ? user.userRole.some((role) => role.roleId === selectedRoleId)
        : true;

      const matchesStatus =
        selectedStatusVal !== null ? user.active === selectedStatusVal : true;

      return matchesSearchText && matchesRoleId && matchesStatus;
    });

    const noOfPages = Math.ceil(filtered.length / pagingInfo.rowsPerPage) || 1;
    const currentPage = Math.min(pagingInfo.currentPage, noOfPages);
    const startIdx = (currentPage - 1) * pagingInfo.rowsPerPage;
    const endIdx = currentPage * pagingInfo.rowsPerPage;
    const currentPageItems = filtered.slice(startIdx, endIdx);

    if (
      pagingInfo.noOfPages !== noOfPages ||
      pagingInfo.currentPage !== currentPage
    ) {
      setPagingInfo((prev) => ({
        ...prev,
        currentPage,
        noOfPages,
      }));
    }

    return currentPageItems;
  }, [
    usersData,
    searchText,
    selectedRole,
    selectedStatus,
    pagingInfo.rowsPerPage,
    pagingInfo.currentPage,
    pagingInfo.noOfPages,
  ]);

  const handleRowPerPageChange = (rowsPerPage) => {
    setPagingInfo((prev) => ({
      ...prev,
      rowsPerPage,
      currentPage: 1,
    }));
  };

  const onPageChange = (page) => {
    setPagingInfo((prev) => ({
      ...prev,
      currentPage: page,
    }));
  };

  const handleAddUser = () => {
    setIsPanelOpen(true);
  };

  const onPanelClose = () => {
    setUserDetails(initalFormData);
    setIsPanelOpen(false);
  };

  const handleChange = useCallback((e) => {
    const { name, type, value, checked } = e.target;

    if (name === "userType") {
      const selectedRoleId = mappedRoleIdsToUserType[value] || "";
      setUserDetails((prev) => ({
        ...prev,
        [name]: value,
        userRole: prev.userRole.map((role) => ({
          ...role,
          roleId: selectedRoleId,
          activeForBallotEmail: false,
          leagueId: "",
        })),
      }));
    } else {
      setUserDetails((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  }, []);

  const handleAddRole = () => {
    const newRole = {
      userRoleId: 0,
      userId: 0,
      roleId: "",
      activeForBallotEmail: false,
      leagueId: "",
    };

    setUserDetails((prevDetails) => ({
      ...prevDetails,
      userRole: [...prevDetails.userRole, newRole],
    }));
  };

  const handleRoleChange = (index, field, value) => {
    const updatedRoles = [...userDetails.userRole];
    updatedRoles[index][field] = value;
    setUserDetails((prevDetails) => ({
      ...prevDetails,
      userRole: updatedRoles,
    }));
  };

  const handleFilterRoleChange = (event) => {
    setSelectedRole(event.target.value);
  };

  const handleFilterStatusChange = (event) => {
    const value = event.target.value === "" ? null : event.target.value === "1";
    setSelectedStatus(value);
  };

  const handleDeleteRow = (userRoleId, indexID) => {
    if (!userRoleId) {
      setUserDetails((prev) => ({
        ...prev,
        userRole: prev.userRole.filter((_, i) => i !== indexID),
      }));
      return;
    }

    deleteRoleRef.current = userRoleId;
    setOpenDeleteRoleDialog(true);
  };

  const onDeleteRoleAgree = async () => {
    await deleteUserRoleDetails(deleteRoleRef.current);
    setOpenDeleteRoleDialog(false);
  };

  const onDeleteRoleDisAgree = async () => {
    deleteRoleRef.current = null;
    setOpenDeleteRoleDialog(false);
  };

  const onSaveHandler = async () => {
    const extractedRoles = userDetails?.userRole?.map((role) => ({
      userRoleId: role.userRoleId,
      userId: role.userId,
      roleId: role.roleId,
      activeForBallotEmail: role.activeForBallotEmail,
      leagueId: role.leagueId,
    }));

    const { userType, teamId } = userDetails;

    const payload = {
      ...userDetails,
      title: ["Admin", "League"].includes(userType) ? "" : userType,
      teamId: userType === "Admin" ? 0 : teamId,
      processedBy: user.loginID,
      userRole: extractedRoles,
    };

    await saveOrUpdateUserDetails(payload);
  };

  const onEditUserHandler = async (userId) => {
    try {
      const users = await fetchUserDetailsWithRoles(userId);
      const userDetails = users[0];

      // console.log("onEditUserHandler", userDetails);

      if (!userDetails) {
        console.error("User details not found");
        return;
      }

      setUserDetails({
        userId: userDetails.userId,
        userType: userDetails.userType,
        firstName: userDetails.firstName,
        lastName: userDetails.lastName,
        loginId: userDetails.loginId,
        email: userDetails.email,
        active: userDetails.active,
        teamId: userDetails.teamId,
        title: userDetails.title,
        rankLevel: userDetails.rankLevel,
        userRole: userDetails.userRole,
      });
      setEditMode(true);

      setIsPanelOpen(true);
    } catch (error) {
      console.error("Failed to fetch user details", error);
    }
  };

  const onCancelAddEdit = () => {
    setUserDetails(initalFormData);
    setIsPanelOpen(false);
  };

  //... Utilities
  const showAlert = (type, message) => {
    setAlert({ open: true, type, message });
  };

  const resetDependecies = () => {
    setUserDetails(initalFormData);
    setIsPanelOpen(false);
    setEditMode(false);
  };

  const clearFilter = () => {
    setSearchText("");
    setSelectedRole("");
    setSelectedStatus(null);
  };

  //...Apis
  const fetchLatestUsersWithRoles = async () => {
    try {
      setLoading(true);
      const { data } = await UserManagementService.getUserWithRoles();
      setUsersData(data || []);
    } catch (error) {
      console.error(`Error fetching :`, error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetailsWithRoles = async (userId) => {
    try {
      setLoading(true);
      const { data = [] } = await UserManagementService.getUserWithRoles(
        parseInt(userId)
      );
      return data;
    } catch (e) {
      console.error("Error - fetchUserDetailsWithRoles:", e);
    } finally {
      setLoading(false);
    }
  };

  const saveOrUpdateUserDetails = async (formObj) => {
    try {
      const { data } = await UserManagementService.addUpdateUser(formObj);
      const resultData = data?.data;
      const error = data?.error;

      const success = Boolean(resultData);
      const alertType = success ? "success" : "warning";
      const action = editMode ? "updated" : "added";
      const message = success
        ? `User details have been ${action} successfully.`
        : error || "An error occurred while performing the action.";

      showAlert(alertType, message);

      if (success) {
        await fetchLatestUsersWithRoles();
        resetDependecies();
      }
    } catch (err) {
      console.error(err);
      showAlert("error", "An error occurred while performing the action...");
    }
  };

  const deleteUserRoleDetails = async (userRoleId) => {
    try {
      const { data } = await UserManagementService.deleteUserRole(
        userRoleId,
        user.loginID
      );
      const success = Boolean(data?.data);
      const message = success
        ? "User role has been deleted successfully..."
        : data?.error || "An error occurred while performing the action.";

      showAlert(success ? "success" : "warning", message);

      if (success) {
        await fetchLatestUsersWithRoles();
        resetDependecies();
      }
    } catch (err) {
      console.error(err);
      showAlert("error", "An error occurred while performing the action...");
    }
  };

  const renderButton = (icon, label, onClick, disabled, startIcon) => {
    return isSmallScreen ? (
      <IconButton color="primary" onClick={onClick} disabled={disabled}>
        {icon}
      </IconButton>
    ) : (
      <Button
        size="small"
        onClick={onClick}
        variant="contained"
        color="primary"
        disabled={disabled}
        startIcon={startIcon}
      >
        {label}
      </Button>
    );
  };

  return (
    <>
      <Loader isLoading={loading} />

      <AppBar
        position="sticky"
        className={classes.appbarBackground}
        sx={{ zIndex: "1098" }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Grid container alignItems="center" spacing={2}>
            <Grid
              item
              lg={6}
              sx={{ display: { xs: "none", sm: "none", md: "block" } }}
            >
              <Typography variant="h6" className={classes.title}>
                Manage Users
              </Typography>
            </Grid>
            <Grid item lg={6} display="flex" justifyContent="flex-end">
              <Stack direction="row" spacing={2} alignItems="center">
                {renderButton(
                  <AddCircleOutlineIcon />,
                  "Add User",
                  handleAddUser,
                  false,
                  <AddCircleOutlineIcon />
                )}
              </Stack>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          m: 2,
          py: 1,
          px: 1,
          backgroundColor: "#f9f9f9",
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
        }}
      >
        <Grid container spacing={2}>
          {/* Search Field */}
          <Grid item xs={12} sm={2}>
            <TextField
              label="🔍 Search by name or email"
              variant="outlined"
              size="medium"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Type a name or email..."
              sx={{
                width: "100%",
                backgroundColor: "#fff",
                borderRadius: 1,
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                "& .MuiOutlinedInput-root:hover": {
                  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                },
              }}
            />
          </Grid>

          {/* Role Select */}
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth>
              <InputLabel id="role-select-label">Select Role</InputLabel>
              <Select
                labelId="role-select-label"
                id="role-select"
                value={selectedRole || ""}
                onChange={handleFilterRoleChange}
                label="Select Role"
              >
                <MenuItem value="">
                  <em>Select Role</em>
                </MenuItem>

                {userRoles.map((role) => (
                  <MenuItem key={role.roleId} value={role.roleId}>
                    {role.roleName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Status Select */}
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth>
              <InputLabel id="status-select-label">Active Status</InputLabel>
              <Select
                labelId="status-select-label"
                id="status-select"
                value={
                  selectedStatus === null
                    ? ""
                    : selectedStatus === true
                    ? "1"
                    : "0"
                }
                onChange={handleFilterStatusChange}
                label="Active Status"
              >
                <MenuItem value="">
                  <em>Select Status</em>
                </MenuItem>
                <MenuItem value="1">Active</MenuItem>
                <MenuItem value="0">In-Active</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={2} display="flex" alignItems="center">
            <Button
              variant="contained"
              onClick={clearFilter}
              startIcon={<ClearIcon />}
              color="error"
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Container maxWidth={false} sx={{ pb: 2 }}>
        <Box>
          <Paging
            pagingInfo={pagingInfo}
            handleRowPerPageChange={handleRowPerPageChange}
            onPageChange={onPageChange}
            className={classes.pagination}
          />
        </Box>

        <Grid container spacing={3}>
          {paginatedUsers.length > 0 ? (
            paginatedUsers.map((user, idx) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                key={idx}
                sx={{ display: "flex" }}
              >
                <UserCard
                  user={user}
                  roles={user.userRole}
                  onEditUser={onEditUserHandler}
                />
              </Grid>
            ))
          ) : (
            <Grid container p={1}>
              <Grid item lg={12}>
                <EmptyUI
                  heading="No results were found based on your search criteria."
                  subHeading={`Modify your criteria to see more results.`}
                  textColor="#000000"
                  icon={<ContactSupportIcon />}
                  iconStyles={{
                    fontSize: "clamp(10rem, 1vw + 8rem, 10rem)",
                    color: "#8c9dc4",
                  }}
                  styles={{
                    minHeight: "70vh",
                  }}
                  actionLabel="Home"
                  actionIcon={<HomeIcon />}
                />
              </Grid>
            </Grid>
          )}
        </Grid>
      </Container>

      <AlertSnackbar
        open={alert.open}
        message={alert.message}
        type={alert.type}
        onClose={() => setAlert({ ...alert, open: false })}
      />

      <ConfirmationDialog
        open={openDeleteRoleDialog}
        message="Are you sure you want to delete?"
        handleClose={() => setOpenDeleteRoleDialog(false)}
        handleAgree={onDeleteRoleAgree}
        handleDisAgree={onDeleteRoleDisAgree}
        maxWidth="sm"
      />

      <SlidingPaneComponent
        isOpen={isPanelOpen}
        title={"Manage User"}
        onRequestClose={onPanelClose}
      >
        <AddEditUser
          userDetails={userDetails}
          handleChange={handleChange}
          userRoles={userRoles}
          allTeams={allTeams}
          onSaveHandler={onSaveHandler}
          onCancelAddEdit={onCancelAddEdit}
          handleAddRole={handleAddRole}
          handleRoleChange={handleRoleChange}
          handleDeleteRow={handleDeleteRow}
          showAlert={showAlert}
          classes={classes}
        />
      </SlidingPaneComponent>
    </>
  );
}

export default UserManagementContainer;
